// Kanban card. Click anywhere on the card opens the edit modal —
// the shell wires that via onClick.
import type { JobDto } from "../../../../../../shared/types/production-schedule";
import type { SolutionTypeDto } from "../../../../../../shared/types/production-schedule";
import { fmtDate } from "../lib/dates";
import {
  isSpoolComplete,
  jobActiveStages,
  jobBaysFor,
  jobStageProgress,
  jobStageTotalHours,
} from "../lib/stageHelpers";
import { PriorityBadge } from "./PriorityBadge";
import { BayChip } from "./BayChip";
import { AssigneeChip } from "./AssigneeChip";
import { useActiveStages } from "./StagesProvider";

interface Props {
  job: JobDto;
  types: SolutionTypeDto[];
  onOpen(id: number): void;
}

export function JobCard({ job, types, onOpen }: Props) {
  const catalogue = useActiveStages();
  const simple = job.simpleMode;
  // Simple jobs roll up from the job-level pipeline; detailed jobs
  // from per-spool completion. A spool is "complete" iff every
  // progression stage (catalogue active rows minus skipped +
  // non-chosen weld bays) is complete — matches the SpoolCard tick.
  const totalHours = simple
    ? jobStageTotalHours(job.jobStages)
    : job.spools.reduce((acc, s) => acc + s.hours, 0);
  const jobProg = simple ? jobStageProgress(job.jobStages) : null;
  const done = simple
    ? jobProg!.complete
    : job.spools.filter((s) => isSpoolComplete({ spool: s, catalogue, jobBay: job.bay })).length;
  const total = simple ? jobProg!.total : job.spools.length;
  const unit = simple ? "job stages" : "spools";
  const miniStages = simple ? jobActiveStages(job.jobStages) : [];
  const stageByKey = new Map(catalogue.map((d) => [d.stageKey, d]));
  const typeName =
    job.solutionType?.name ??
    types.find((t) => t.id === job.solutionType?.id)?.name ??
    "";
  return (
    <div
      className="proj-card"
      onClick={() => onOpen(job.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen(job.id);
      }}
    >
      <div className="proj-card-top">
        <span className="proj-id">{job.jobNumber}</span>
        <PriorityBadge priority={job.priority} />
      </div>
      <div className="proj-name">{job.name}</div>
      <div className="proj-customer">{job.customer ?? "—"}</div>
      <div className="proj-tags">
        {typeName && <span className="proj-tag">{typeName}</span>}
        {simple && (
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "var(--bsp-fg-muted)",
            border: "1px solid var(--bsp-border-strong)",
            borderRadius: 999,
            padding: "1px 7px",
          }}>Simple</span>
        )}
        <BayChip bays={jobBaysFor({ spools: job.spools, jobBay: job.bay })} />
      </div>
      {simple && miniStages.length > 0 && (
        <div style={{ display: "flex", gap: 2, marginTop: 6 }}>
          {miniStages.map((st) => {
            const def = stageByKey.get(st.stageKey);
            return (
              <span
                key={st.stageKey}
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 2,
                  background: def?.color ?? "#9CA09F",
                  opacity: st.status === "complete" ? 1 : st.status === "in-progress" ? 0.6 : 0.25,
                }}
                title={`${def?.name ?? st.stageKey} · ${st.status}`}
              />
            );
          })}
        </div>
      )}
      {total > 0 && (
        <div className="spool-summary">
          {done}/{total} {unit} complete · {totalHours.toFixed(1)}h workshop
        </div>
      )}
      <div className="proj-footer">
        <span className="proj-date">Due {fmtDate(job.dueDate)}</span>
        <AssigneeChip member={job.assignee} />
      </div>
    </div>
  );
}
